import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useT } from '../i18n/i18n';
import Celebration from './Celebration';
import { BASE, HOME, LANE, LAST_TRACK, START, TRACK, choose, isSafe, move, movable, newGame, steps, trackCell, won, type Side, type State } from './ludoRules';

export type LudoOptions = { opponent: 'computer' | 'person'; pieces: number; needSix: boolean; motion: boolean };

export const LUDO_DEFAULTS: LudoOptions = { opponent: 'computer', pieces: 2, needSix: false, motion: true };

export function LudoOptionsForm({ value, onChange }: { value: LudoOptions; onChange: (v: LudoOptions) => void }) {
  const t = useT();
  return (
    <>
      <div className="field">
        <label htmlFor="ld-opp">{t('Play against')}</label>
        <select id="ld-opp" value={value.opponent} onChange={(e) => onChange({ ...value, opponent: e.target.value as LudoOptions['opponent'] })}>
          <option value="computer">{t('The computer')}</option>
          <option value="person">{t('Another person')}</option>
        </select>
      </div>
      <div className="field">
        <label htmlFor="ld-pieces">{t('Pieces each')}</label>
        <select id="ld-pieces" value={value.pieces} onChange={(e) => onChange({ ...value, pieces: Number(e.target.value) })}>
          <option value={2}>{t('2 — short game')}</option>
          <option value={3}>{t('3')}</option>
          <option value={4}>{t('4 — long game')}</option>
        </select>
      </div>
      <label className="check">
        <input type="checkbox" checked={value.needSix} onChange={(e) => onChange({ ...value, needSix: e.target.checked })} />
        {t('A piece needs a 6 to come out')}
      </label>
      <label className="check">
        <input type="checkbox" checked={value.motion} onChange={(e) => onChange({ ...value, motion: e.target.checked })} />
        {t('Moving pieces: the dice rolls and pieces hop')}
      </label>
    </>
  );
}

const R = 37;
const LANE_STEP = 6.4;
const BASE_AT: Record<Side, [number, number]> = { 0: [14, 14], 1: [86, 86] };
const SLOTS: [number, number][] = [[-4.6, -4.6], [4.6, -4.6], [-4.6, 4.6], [4.6, 4.6]];
const angle = (cell: number) => Math.PI + (cell * 2 * Math.PI) / TRACK;
const cellXY = (cell: number): [number, number] => [50 + R * Math.cos(angle(cell)), 50 + R * Math.sin(angle(cell))];
const laneXY = (side: Side, k: number): [number, number] => [side === 0 ? 50 - R + LANE_STEP * k : 50 + R - LANE_STEP * k, 50];

function spot(side: Side, p: number, piece: number): [number, number] {
  if (p === BASE) { const [bx, by] = BASE_AT[side]; const [dx, dy] = SLOTS[piece]; return [bx + dx, by + dy]; }
  if (p === HOME) return [side === 0 ? 46 : 54, 50 - 4.5 + piece * 3];
  if (p > LAST_TRACK) return laneXY(side, p - LAST_TRACK);
  return cellXY(trackCell(side, p));
}

const PIPS: Record<number, number[]> = { 1: [4], 2: [0, 8], 3: [0, 4, 8], 4: [0, 2, 6, 8], 5: [0, 2, 4, 6, 8], 6: [0, 2, 3, 5, 6, 8] };
const SHOW: Record<number, string> = { 1: 'rotateX(0deg) rotateY(0deg)', 2: 'rotateY(-90deg)', 3: 'rotateY(-180deg)', 4: 'rotateY(90deg)', 5: 'rotateX(-90deg)', 6: 'rotateX(90deg)' };
const FACES: [number, string][] = [[1, 'rotateY(0deg)'], [2, 'rotateY(90deg)'], [3, 'rotateY(180deg)'], [4, 'rotateY(-90deg)'], [5, 'rotateX(90deg)'], [6, 'rotateX(-90deg)']];

type Last = { seq: number; side: Side; piece: number; path: [number, number][]; captured: { piece: number; from: [number, number] }[] };
type Phase = 'roll' | 'rolling' | 'move' | 'busy' | 'over';

export default function Ludo({ options, onAgain }: { options: LudoOptions; onAgain: () => void }) {
  const t = useT();
  const rules = useMemo(() => ({ pieces: options.pieces, needSix: options.needSix }), [options.pieces, options.needSix]);
  const [state, setState] = useState<State>(() => newGame(rules));
  const [side, setSide] = useState<Side>(0);
  const [roll, setRoll] = useState<number | null>(null);
  const [phase, setPhase] = useState<Phase>('roll');
  const [note, setNote] = useState<string | null>(null);
  const [last, setLast] = useState<Last | null>(null);
  const board = useRef<HTMLDivElement | null>(null);
  const die = useRef<HTMLDivElement | null>(null);
  const tokens = useRef(new Map<string, HTMLButtonElement>());
  const timers = useRef<number[]>([]);
  const name = (s: Side) => (s === 0 ? t('Red') : t('Yellow'));
  const computer = (s: Side) => options.opponent === 'computer' && s === 1;
  const choices = phase === 'move' && roll !== null ? movable(rules, state, side, roll) : [];
  const winner = won(state, 0) ? 0 : won(state, 1) ? 1 : null;
  const STEP_MS = options.motion ? 170 : 0;

  const later = (fn: () => void, ms: number) => { timers.current.push(window.setTimeout(fn, ms)); };
  useEffect(() => () => timers.current.forEach((id) => window.clearTimeout(id)), []);

  function pass(next: Side) {
    setSide(next);
    setRoll(null);
    setPhase('roll');
  }

  function doRoll() {
    if (phase !== 'roll') return;
    const value = 1 + Math.floor(Math.random() * 6);
    setPhase('rolling');
    setNote(null);
    if (options.motion && die.current) {
      const spin = `rotateX(${720 + 90 * Math.floor(Math.random() * 4)}deg) rotateY(${540 + 90 * Math.floor(Math.random() * 4)}deg)`;
      die.current.animate([{ transform: spin }, { transform: SHOW[value] }], { duration: 650, easing: 'cubic-bezier(.2,.7,.3,1)' });
    }
    later(() => {
      setRoll(value);
      const options2 = movable(rules, state, side, value);
      if (options2.length === 0) {
        setNote(t('{name} rolled {n}. No piece can move.', { name: name(side), n: value }));
        setPhase('busy');
        later(() => pass((1 - side) as Side), 1300);
      } else {
        setPhase('move');
      }
    }, options.motion ? 680 : 120);
  }

  function play(piece: number) {
    if (phase !== 'move' || roll === null || !choices.includes(piece)) return;
    const m = move(rules, state, side, piece, roll);
    if (!m) return;
    const path = steps(m.from, m.to).map((p) => spot(side, p, piece));
    const other = (1 - side) as Side;
    const captured = m.captured.map((i) => ({ piece: i, from: spot(other, state.pos[other][i], i) }));
    setLast((l) => ({ seq: (l?.seq ?? 0) + 1, side, piece, path: [spot(side, m.from, piece), ...path], captured }));
    setState(m.next);
    setPhase('busy');
    const parts: string[] = [];
    if (m.captured.length) parts.push(t('{name} sent a {other} piece back to its base.', { name: name(side), other: name(other) }));
    if (m.to === HOME) parts.push(t('A {name} piece reached the centre.', { name: name(side) }));
    setNote(parts.join(' ') || null);
    const wait = path.length * STEP_MS + (captured.length ? 450 : 0) + 120;
    later(() => {
      if (won(m.next, side)) { setPhase('over'); return; }
      if (roll === 6) { setRoll(null); setPhase('roll'); setNote((n) => [n, t('A 6 — {name} rolls again.', { name: name(side) })].filter(Boolean).join(' ')); return; }
      pass(other);
    }, wait);
  }

  const scheduled = useRef('');
  useEffect(() => {
    if (!computer(side) || winner !== null) return;
    const key = [side, phase, roll, last?.seq ?? 0].join('|');
    if (scheduled.current === key) return;
    scheduled.current = key;
    if (phase === 'roll') later(doRoll, 700);
    if (phase === 'move' && roll !== null) {
      const pick = choose(rules, state, side, roll);
      if (pick !== null) later(() => play(pick), 650);
    }
  });

  useLayoutEffect(() => {
    if (!last || !options.motion || !board.current) return;
    const w = board.current.getBoundingClientRect().width / 100;
    const el = tokens.current.get(`${last.side}-${last.piece}`);
    const [ex, ey] = last.path[last.path.length - 1];
    if (el && last.path.length > 1) {
      const frames: Keyframe[] = [];
      last.path.forEach(([x, y], i) => {
        const at = `translate(-50%, -50%) translate(${(x - ex) * w}px, ${(y - ey) * w}px)`;
        if (i > 0) {
          const [px, py] = last.path[i - 1];
          frames.push({ transform: `translate(-50%, -50%) translate(${((x + px) / 2 - ex) * w}px, ${((y + py) / 2 - ey) * w - 10}px) scale(1.18)` });
        }
        frames.push({ transform: `${at} scale(1)` });
      });
      el.animate(frames, { duration: (last.path.length - 1) * STEP_MS, easing: 'linear' });
    }
    const other = (1 - last.side) as Side;
    last.captured.forEach(({ piece, from }) => {
      const c = tokens.current.get(`${other}-${piece}`);
      if (!c) return;
      const [bx, by] = spot(other, BASE, piece);
      c.animate(
        [{ transform: `translate(-50%, -50%) translate(${(from[0] - bx) * w}px, ${(from[1] - by) * w}px)` }, { transform: `translate(-50%, -50%) translate(${(from[0] - bx) * w * 0.5}px, ${(from[1] - by) * w * 0.5 - 30}px) scale(1.2)` }, { transform: 'translate(-50%, -50%)' }],
        { duration: 450, delay: (last.path.length - 1) * STEP_MS, easing: 'ease-in-out', fill: 'backwards' },
      );
    });
  }, [last, options.motion, STEP_MS]);

  const status = winner !== null
    ? t('{name} wins!', { name: name(winner) })
    : phase === 'move' ? t('{name} rolled {n}. Choose a piece to move.', { name: name(side), n: roll ?? 0 })
      : computer(side) ? t('The computer is playing Yellow.')
        : t('{name} to roll.', { name: name(side) });

  const pieces = ([0, 1] as Side[]).flatMap((s) => state.pos[s].map((p, i) => ({ s, p, i })));
  const stack = new Map<string, number>();

  return (
    <div className="lz-ludo">
      <p className="lz-status" aria-live="polite">{status}</p>
      <p className="lz-message" role="status">{note ?? ' '}</p>
      <div className="lz-ld-stage">
        {winner !== null && <Celebration text={t('{name} wins!', { name: name(winner) })} motion={options.motion} />}
        <div className="lz-ld-board" ref={board} role="group" aria-label={t('Ludo board')}>
          {([0, 1] as Side[]).map((s) => (
            <span key={`base-${s}`} className={`lz-ld-base is-${s}`} style={{ left: `${BASE_AT[s][0]}%`, top: `${BASE_AT[s][1]}%` }} />
          ))}
          {Array.from({ length: TRACK }, (_, c) => {
            const [x, y] = cellXY(c);
            const owner = c === START[0] ? 0 : c === START[1] ? 1 : null;
            return (
              <span
                key={c}
                className={`lz-ld-cell${owner !== null ? ` is-start is-${owner}` : ''}${isSafe(c) ? ' is-safe' : ''}`}
                style={{ left: `${x}%`, top: `${y}%`, transform: `translate(-50%, -50%) rotate(${(angle(c) * 180) / Math.PI + 90}deg)` }}
              />
            );
          })}
          {([0, 1] as Side[]).flatMap((s) => Array.from({ length: LANE }, (_, k) => {
            const [x, y] = laneXY(s, k + 1);
            return <span key={`lane-${s}-${k}`} className={`lz-ld-cell is-lane is-${s}`} style={{ left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)' }} />;
          }))}
          <span className="lz-ld-home" />
          {pieces.map(({ s, p, i }) => {
            let [x, y] = spot(s, p, i);
            if (p !== BASE && p !== HOME) {
              const k = `${Math.round(x * 10)},${Math.round(y * 10)}`;
              const n = stack.get(k) ?? 0;
              stack.set(k, n + 1);
              x += n * 1.6;
              y -= n * 1.6;
            }
            const can = !computer(s) && side === s && choices.includes(i);
            const where = p === BASE ? t('in its base') : p === HOME ? t('in the centre') : p > LAST_TRACK ? t('in the home lane') : t('on the track');
            return (
              <button
                key={`${s}-${i}`}
                ref={(el) => { const k = `${s}-${i}`; if (el) tokens.current.set(k, el); else tokens.current.delete(k); }}
                type="button"
                className={`lz-ld-token is-${s}${can ? ' can-move' : ''}`}
                style={{ left: `${x}%`, top: `${y}%` }}
                disabled={!can}
                aria-label={t('{name} piece {n}, {where}', { name: name(s), n: i + 1, where })}
                onClick={() => play(i)}
              />
            );
          })}
          <button
            type="button"
            className={`lz-ld-dice${phase === 'roll' && !computer(side) ? ' can-roll' : ''} is-${side}`}
            disabled={phase !== 'roll' || computer(side) || winner !== null}
            aria-label={roll ? t('Dice showing {n}. Roll the dice', { n: roll }) : t('Roll the dice')}
            onClick={doRoll}
          >
            <span className="lz-die" ref={die} style={{ transform: SHOW[roll ?? 1] }}>
              {FACES.map(([v, place]) => (
                <span key={v} className="lz-die-face" style={{ transform: `${place} translateZ(var(--half))` }}>
                  {Array.from({ length: 9 }, (_, k) => <span key={k} className={PIPS[v].includes(k) ? 'lz-pip' : ''} />)}
                </span>
              ))}
            </span>
          </button>
        </div>
      </div>
      <div className="toolbar lz-actions">
        <button type="button" className={winner !== null ? 'btn' : 'btn secondary'} onClick={onAgain}>{winner !== null ? t('Play again') : t('New game')}</button>
      </div>
    </div>
  );
}
